import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import {
  LuArrowDownWideNarrow,
  LuArrowUpDown,
  LuCalendarClock,
  LuDatabase,
  LuFileText,
  LuFilter,
  LuHome,
  LuPlus,
  LuSearch,
  LuUpload,
} from "react-icons/lu";
import { toast } from "react-toastify";

import ExportMenu from "../../components/importExport/ExportMenu";
import ImportModal from "../../components/importExport/ImportModal";
import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import PartyFormModal from "../../components/parties/PartyFormModal";
import PartyList from "../../components/parties/PartyList";
import Pagination from "../../components/pagination";
import Button from "../../components/ui/Button";
import FilterSelect, { FilterOption } from "../../components/ui/FilterSelect";
import Input from "../../components/ui/Input";
import { sortLabelsOf } from "../../components/ui/SortableTableHead";
import cx from "../../components/ui/cx";
import {
  actionBarClass,
  buttonSpinnerClass,
  cardClass,
  cardContentClass,
  cardHeaderClass,
  cardTitleClass,
  fieldIconClass,
  fieldInputClass,
  filterFooterClass,
  filterGridClass,
  filterLabelClass,
  pageTitleClass,
  summaryLabelClass,
  summaryValueClass,
} from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchPartiesListData,
  getEditParty,
  getParties,
  getTotalParties,
  getTotalPendingAmount,
  setEditParty,
  togglePartyModal,
} from "../../features/parties/partiesSlice";
import {
  getListState,
  saveListState,
} from "../../features/listState/listStateSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { updatePdfFile } from "../../features/settings/settingsSlice";
import { DELETE, GET, POST, PUT } from "../../services/axios.service";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { iconSelectStyles, toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";
import {
  IFormInput,
  PartyDateBy,
  PartySortBy,
  PartySortOrder,
} from "../../types/parties";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "પાર્ટીઓ" },
];

const listKey = "parties";

const inhouseOptions = [
  { value: 1, label: "હા" },
  { value: 0, label: "ના" },
];

const dateByOptions: FilterOption<PartyDateBy>[] = [
  { value: "created_at", label: "બનાવ્યા તારીખ" },
  { value: "updated_at", label: "સુધાર્યા તારીખ" },
];

const sortByOptions: FilterOption<PartySortBy>[] = [
  { value: "id", label: "ID" },
  { value: "firm_name", label: "પેઢી નું નામ" },
  { value: "name", label: "પાર્ટી નું નામ" },
  { value: "total_amount", label: "પેમેન્ટ" },
  { value: "status", label: "સ્ટેટ્સ" },
  { value: "created_at", label: "બનાવ્યા તારીખ" },
  { value: "updated_at", label: "સુધાર્યા તારીખ" },
];

const sortLabels = sortLabelsOf(sortByOptions);

const sortOrderOptions: FilterOption<PartySortOrder>[] = [
  { value: "asc", label: "ચડતો ક્રમ" },
  { value: "desc", label: "ઉતરતો ક્રમ" },
];

const formatAmount = (value: number) =>
  (value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const reportPeriodOptions = [
  { value: "all", label: "બધો સમય" },
  { value: "month", label: "મહિનો પસંદ કરો" },
];

const monthOptions = [
  { value: "01", label: "જાન્યુઆરી" },
  { value: "02", label: "ફેબ્રુઆરી" },
  { value: "03", label: "માર્ચ" },
  { value: "04", label: "એપ્રિલ" },
  { value: "05", label: "મે" },
  { value: "06", label: "જૂન" },
  { value: "07", label: "જુલાઈ" },
  { value: "08", label: "ઓગસ્ટ" },
  { value: "09", label: "સપ્ટેમ્બર" },
  { value: "10", label: "ઓક્ટોબર" },
  { value: "11", label: "નવેમ્બર" },
  { value: "12", label: "ડિસેમ્બર" },
];

const firstReportYear = 2024;
const yearOptions = Array.from(
  { length: new Date().getFullYear() + 1 - firstReportYear + 1 },
  (_, i) => {
    const year = String(new Date().getFullYear() + 1 - i);
    return { value: year, label: year };
  }
);

const currentMonthValue = () =>
  String(new Date().getMonth() + 1).padStart(2, "0");

const PartiesPage = () => {
  const dispatch = useAppDispatch();
  const parties = useAppSelector(getParties);
  const totalParties = useAppSelector(getTotalParties);
  const editParty = useAppSelector(getEditParty);
  const totalPendingAmount = useAppSelector(getTotalPendingAmount);
  const loggingUser = useAppSelector(getLoggingUser);
  const savedList = useAppSelector((state) => getListState(state, listKey));

  const canCreate = hasPermission(
    loggingUser?.permissions,
    permission.partiesCreate
  );
  const canReport = hasPermission(
    loggingUser?.permissions,
    permission.partiesReport
  );
  // The import updates the parties a file names by a saved firm name only for a user who may update parties.
  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.partiesUpdate
  );

  interface FilterFormInput {
    name: string;
    fromDate: string;
    toDate: string;
    is_inhouse?: number;
    date_by: PartyDateBy;
    sort_by: PartySortBy;
    sort_order: PartySortOrder;
  }

  // The dates filter the date a party was added, and active parties come first, newest first, until the user picks
  // otherwise.
  const emptyFilters: FilterFormInput = {
    name: "",
    fromDate: "",
    toDate: "",
    date_by: "created_at",
    sort_by: "status",
    sort_order: "desc",
  };

  // The filters in use when the user last left the list (to open a party, say), else none.
  const savedFilters: FilterFormInput = savedList.filters || emptyFilters;

  const {
    handleSubmit,
    register,
    reset,
    getValues,
    control,
    setValue,
    watch,
  } = useForm<FilterFormInput>({ defaultValues: savedFilters });

  const [page, setPage] = useState<number>(savedList.page || 1);
  const [pageSize, setPageSize] = useState<number>(savedList.pageSize || 50);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isReportLoading, setIsReportLoading] = useState<boolean>(false);
  const [showFilter, setShowFilter] = useState<boolean>(
    !!savedList.showFilter
  );
  const [showReportFilter, setShowReportFilter] = useState<boolean>(
    !!savedList.showReportFilter
  );
  const [showImport, setShowImport] = useState(false);
  // The order the list on screen was loaded in, which the table headers show. The filter's selects may hold an
  // order that isn't applied yet.
  const [appliedSort, setAppliedSort] = useState<{
    sortBy: PartySortBy;
    sortOrder: PartySortOrder;
  }>({ sortBy: savedFilters.sort_by, sortOrder: savedFilters.sort_order });
  const [reportPeriod, setReportPeriod] = useState<"all" | "month">(
    savedList.reportPeriod || "all"
  );
  const [reportMonth, setReportMonth] = useState<string>(
    (savedList.reportPeriod === "month" && savedList.reportMonth) ||
      currentMonthValue()
  );
  const [reportYear, setReportYear] = useState<string>(
    (savedList.reportPeriod === "month" && savedList.reportYear) ||
      String(new Date().getFullYear())
  );

  const fetchId = useRef(0);

  const paginate = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const handleShowPartyForm = async () => {
    await dispatch(setEditParty(null));
    dispatch(togglePartyModal(true));
  };

  const handleSaveParty = async (data: IFormInput) => {
    let saved = false;

    try {
      if (editParty && editParty.id) {
        await PUT(`/v1/admin/parties/${editParty.id}`, {
          type: data.type,
          is_inhouse: data.is_inhouse,
          firm_name: data.firm_name,
          name: data.name,
          phone: data.phone,
          wp_phone: data.wp_phone,
          address: data.address,
          credit_limit: data.credit_limit,
          status: data.status,
        })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              saved = true;

              await dispatch(setEditParty(null));

              if (page === 1) {
                fetchPartiesList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      } else {
        await POST("/v1/admin/parties", {
          type: data.type,
          is_inhouse: data.is_inhouse,
          firm_name: data.firm_name,
          name: data.name,
          phone: data.phone,
          wp_phone: data.wp_phone,
          address: data.address,
          credit_limit: data.credit_limit,
          status: data.status,
        })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              saved = true;

              if (page === 1) {
                fetchPartiesList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      }
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }

    return saved;
  };

  const handleDeleteParty = async (id: number) => {
    try {
      await DELETE(`/v1/admin/parties/${id}`)
        .then(async (response: any) => {
          if (response.data.status === "success") {
            const pageNumber = parties.length > 1 ? page : page - 1;
            if (page === pageNumber || pageNumber < 1) {
              fetchPartiesList(page, pageSize);
            } else {
              setPage(pageNumber);
            }

            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }
  };

  // A new filter, order or import starts again from the first page.
  const showFirstPage = () => {
    if (page === 1) {
      fetchPartiesList(page, pageSize);
    } else {
      setPage(1);
    }
  };

  const handleOnFilterSubmit = async () => {
    showFirstPage();
  };

  const handleClearFilter = () => {
    reset(emptyFilters);
    showFirstPage();
  };

  // Filter values shared by the list and the export.
  const filterParams = () => {
    const data = getValues();

    return {
      search: data?.name || null,
      fromDate: data?.fromDate || null,
      toDate: data?.toDate || null,
      // ના (0) is a real filter value, so only an empty choice is left out.
      is_inhouse:
        data?.is_inhouse === 0 || data?.is_inhouse === 1
          ? data.is_inhouse
          : null,
      date_by: data.date_by,
      sort_by: data.sort_by,
      sort_order: data.sort_order,
    };
  };

  const fetchPartiesList = async (page: number, size: number) => {
    const currentFetch = ++fetchId.current;

    setIsLoading(true);

    const params = filterParams();

    dispatch(saveListState({ key: listKey, value: { filters: getValues() } }));
    setAppliedSort({ sortBy: params.sort_by, sortOrder: params.sort_order });

    dispatch(
      fetchPartiesListData({
        page: page,
        size: size,
        ...params,
      })
    ).then((action: any) => {
      const result = action?.payload?.data;

      if (currentFetch === fetchId.current && result?.status !== "success") {
        toast.error(
          result?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
      }

      // A saved page that is now past the end (its rows deleted, say) moves to the last page.
      const lastPage = Math.max(
        1,
        Math.ceil((Number(result?.data?.total_records) || 0) / size)
      );

      if (
        currentFetch === fetchId.current &&
        page > 1 &&
        result?.status === "success" &&
        !result?.data?.records?.length &&
        lastPage < page
      ) {
        setPage(lastPage);
        return;
      }

      setIsLoading(false);
    });
  };

  // A table header sorts the list as the filter's ગોઠવો and ક્રમ do, and they show the same choice. A new order
  // starts again from the first page.
  const handleSort = (sortBy: PartySortBy, sortOrder: PartySortOrder) => {
    setValue("sort_by", sortBy);
    setValue("sort_order", sortOrder);
    showFirstPage();
  };

  const handleClearReportFilter = () => {
    setReportPeriod("all");
    setReportMonth(currentMonthValue());
    setReportYear(String(new Date().getFullYear()));
  };

  const handleGeneratePaymentReport = async () => {
    try {
      setIsReportLoading(true);
      const params =
        reportPeriod === "month"
          ? { month: `${reportYear}-${reportMonth}` }
          : {};

      await GET(`/v1/admin/parties/payment/reports`, params)
        .then(async (response: any) => {
          if (response.data.status === "success") {
            toast.success(response.data.message, {
              ...(toastStyles as any),
            });

            dispatch(updatePdfFile(response.data.data));
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    } finally {
      setIsReportLoading(false);
    }
  };

  // Coming back to the list (from a record's page, say) shows the same page and filters.
  useEffect(() => {
    dispatch(
      saveListState({
        key: listKey,
        value: {
          page,
          pageSize,
          showFilter,
          showReportFilter,
          reportPeriod,
          reportMonth,
          reportYear,
        },
      })
    );
  }, [
    dispatch,
    page,
    pageSize,
    showFilter,
    showReportFilter,
    reportPeriod,
    reportMonth,
    reportYear,
  ]);

  useEffect(() => {
    fetchPartiesList(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>પાર્ટી મેનેજમેન્ટ</h1>
      </div>

      <div className="space-y-4 md:space-y-6">
        <div className={actionBarClass}>
          <div className="flex items-center gap-2">
            <span className={summaryLabelClass}>ટોટલ પેમેન્ટ:</span>
            <span className={summaryValueClass}>
              {formatAmount(totalPendingAmount)}
            </span>
          </div>

          <div
            className={cx(
              "grid w-full gap-2 sm:flex sm:w-auto",
              canCreate || canReport ? "grid-cols-2" : "grid-cols-1"
            )}
          >
            {canReport && (
              <Button
                variant="outline"
                onClick={() => {
                  if (!showReportFilter === true) setShowFilter(false);
                  setShowReportFilter(!showReportFilter);
                }}
              >
                <LuFileText className="h-4 w-4" />
                પેમેન્ટ રિપોર્ટ
              </Button>
            )}

            <Button
              variant="outline"
              onClick={() => {
                if (!showFilter === true) setShowReportFilter(false);
                setShowFilter(!showFilter);
              }}
            >
              <LuFilter className="h-4 w-4" />
              ફિલ્ટર
            </Button>

            {canCreate && (
              <Button variant="outline" onClick={() => setShowImport(true)}>
                <LuUpload className="h-4 w-4" />
                ઇમ્પોર્ટ
              </Button>
            )}

            {canCreate && (
              <Button
                variant="primary"
                className={cx(!canReport && "col-span-2")}
                onClick={handleShowPartyForm}
              >
                <LuPlus className="h-4 w-4" />
                નવી પાર્ટી ઉમેરો
              </Button>
            )}
          </div>
        </div>

        {showReportFilter && (
          <div className={cardClass}>
            <div className={cardHeaderClass}>
              <div className={`${cardTitleClass} text-lg`}>પેમેન્ટ રિપોર્ટ</div>
            </div>

            <div className={cardContentClass}>
              <div className={filterGridClass}>
                <div className="space-y-2">
                  <label className={filterLabelClass}>સમયગાળો</label>
                  <div className="relative">
                    <LuDatabase className={fieldIconClass} />
                    <Select
                      styles={iconSelectStyles}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      placeholder="સમયગાળો"
                      options={reportPeriodOptions}
                      value={
                        reportPeriodOptions.find(
                          (val) => val.value === reportPeriod
                        ) || null
                      }
                      onChange={(val: any) =>
                        setReportPeriod(val?.value === "month" ? "month" : "all")
                      }
                    />
                  </div>
                </div>

                {reportPeriod === "month" && (
                  <>
                    <div className="space-y-2">
                      <label className={filterLabelClass}>મહિનો</label>
                      <div className="relative">
                        <LuDatabase className={fieldIconClass} />
                        <Select
                          styles={iconSelectStyles}
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="મહિનો"
                          options={monthOptions}
                          value={
                            monthOptions.find(
                              (val) => val.value === reportMonth
                            ) || null
                          }
                          onChange={(val: any) =>
                            val?.value && setReportMonth(val.value)
                          }
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className={filterLabelClass}>વર્ષ</label>
                      <div className="relative">
                        <LuDatabase className={fieldIconClass} />
                        <Select
                          styles={iconSelectStyles}
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="વર્ષ"
                          options={yearOptions}
                          value={
                            yearOptions.find(
                              (val) => val.value === reportYear
                            ) || null
                          }
                          onChange={(val: any) =>
                            val?.value && setReportYear(val.value)
                          }
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className={filterFooterClass}>
                <Button
                  type="button"
                  variant="outline"
                  className="max-sm:flex-1"
                  onClick={handleClearReportFilter}
                >
                  ફિલ્ટર સાફ કરો
                </Button>

                <Button
                  type="button"
                  variant="primary"
                  className="max-sm:flex-1"
                  onClick={handleGeneratePaymentReport}
                  disabled={isReportLoading}
                >
                  {isReportLoading && <span className={buttonSpinnerClass} />}
                  રિપોર્ટ બનાવો
                </Button>
              </div>
            </div>
          </div>
        )}

        {showFilter && (
          <div className={cardClass}>
            <div className={cardHeaderClass}>
              <div className={`${cardTitleClass} text-lg`}>ફિલ્ટર</div>
            </div>

            <div className={cardContentClass}>
              <form onSubmit={handleSubmit(handleOnFilterSubmit)}>
                <div className={filterGridClass}>
                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ થી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        className={fieldInputClass()}
                        {...register("fromDate")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ સુધી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        className={fieldInputClass()}
                        {...register("toDate")}
                      />
                    </div>
                  </div>

                  <FilterSelect
                    label="તારીખ ફિલ્ટર"
                    icon={LuCalendarClock}
                    placeholder="તારીખ સિલેક્ટ કરો"
                    options={dateByOptions}
                    value={watch("date_by")}
                    onChange={(value) => value && setValue("date_by", value)}
                  />

                  <div className="space-y-2">
                    <label className={filterLabelClass}>શોધો</label>
                    <div className="relative">
                      <LuSearch className={fieldIconClass} />
                      <Input
                        placeholder="વિગતો દ્વારા શોધો"
                        className={fieldInputClass()}
                        {...register("name")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>ઇનહાઉસ</label>
                    <div className="relative">
                      <LuHome className={fieldIconClass} />
                      <Controller
                        {...register("is_inhouse")}
                        control={control}
                        render={({ field }) => (
                          <Select
                            isClearable
                            {...field}
                            value={
                              inhouseOptions.find(
                                (val) => val.value === field.value
                              ) || null
                            }
                            styles={iconSelectStyles}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder="ઇનહાઉસ સિલેક્ટ કરો"
                            options={inhouseOptions}
                            onChange={(val: any) =>
                              setValue("is_inhouse", val?.value)
                            }
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* The two sort selects start a row together, as on the material orders filter. */}
                  <div className="md:col-start-1">
                    <FilterSelect
                      label="ગોઠવો"
                      icon={LuArrowUpDown}
                      placeholder="ગોઠવણી સિલેક્ટ કરો"
                      options={sortByOptions}
                      value={watch("sort_by")}
                      onChange={(value) => value && setValue("sort_by", value)}
                    />
                  </div>

                  <FilterSelect
                    label="ક્રમ"
                    icon={LuArrowDownWideNarrow}
                    placeholder="ક્રમ સિલેક્ટ કરો"
                    options={sortOrderOptions}
                    value={watch("sort_order")}
                    onChange={(value) =>
                      value && setValue("sort_order", value)
                    }
                  />
                </div>

                <div className={filterFooterClass}>
                  <Button
                    type="button"
                    variant="outline"
                    className="max-sm:flex-1"
                    onClick={handleClearFilter}
                  >
                    ફિલ્ટર સાફ કરો
                  </Button>

                  {canReport && (
                    <ExportMenu
                      endpoint="/v1/admin/parties/export"
                      getParams={filterParams}
                    />
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    className="max-sm:flex-1"
                  >
                    ફિલ્ટર લાગુ કરો
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        <PartyList
          parties={parties}
          pageNumber={page}
          pageSize={pageSize}
          isLoading={isLoading}
          sortBy={appliedSort.sortBy}
          sortOrder={appliedSort.sortOrder}
          onSort={handleSort}
          sortLabels={sortLabels}
          handleDeleteParty={handleDeleteParty}
        />

        {!isLoading && parties.length > 0 && (
          <Pagination
            itemsPerPage={pageSize}
            totalItems={totalParties}
            paginate={paginate}
            currentPage={page}
            onPageSizeChange={handlePageSizeChange}
            itemLabel="પાર્ટી"
          />
        )}
      </div>

      <PartyFormModal handleSaveParty={handleSaveParty} />

      <ImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        title="પાર્ટી ઇમ્પોર્ટ કરો"
        endpoint="/v1/admin/parties"
        help={[
          "કોલમ: પ્રકાર, ઇનહાઉસ, પેઢીનું નામ, પાર્ટીનું નામ, મોબાઇલ નંબર, વહાર્ટસપ નંબર, એડ્રેસ, ક્રેડિટ લિમિટ, સ્ટેટ્સ.",
          "પ્રકાર અને પેઢીનું નામ જરૂરી છે. પ્રકાર: મટીરીયલ, કટીંગ, ટર્નિંગ, VMC. એક થી વધુ પ્રકાર હોય તો , થી અલગ લખો (દા.ત. કટીંગ, VMC).",
          "ઇનહાઉસ ખાલી હોય તો ના, ક્રેડિટ લિમિટ ખાલી હોય તો 0 અને સ્ટેટ્સ ખાલી હોય તો સક્રિય ગણાશે. ઇનહાઉસ માં હા અથવા ના, સ્ટેટ્સ માં સક્રિય અથવા નિષ્ક્રિય લખો.",
          ...(canUpdate
            ? [
                "પહેલેથી ઉમેરેલા પેઢીના નામ વાળી પાર્ટી ફરીથી નહીં બને, ફાઇલ મુજબ સુધારાશે: એક્સપોર્ટ કરેલી ફાઇલ માં ફેરફાર કરીને ઇમ્પોર્ટ કરો.",
                "પાર્ટી તેના પેઢીના નામ થી ઓળખાય છે: ફાઇલ માં બદલેલું પેઢીનું નામ નવી પાર્ટી બનાવે છે, તેથી તે પાર્ટી પેજ પર બદલો.",
                "જે પ્રકાર (કટીંગ, ટર્નિંગ કે VMC) ના ભાવ પાર્ટી માં ઉમેરેલા હોય તે પ્રકાર ફાઇલ થી કાઢી શકાશે નહીં, તે પાર્ટી પેજ પર કાઢો.",
              ]
            : ["પહેલેથી ઉમેરેલા પેઢીના નામ વાળી પાર્ટી છોડી દેવામાં આવશે."]),
        ]}
        onImported={showFirstPage}
      />
    </AppLayout>
  );
};
export default PartiesPage;
